import { PortalDialogProps } from "@app/services/modal.service";
import Modal from "../modal/modal.component";
import { useEffect, useState } from "react";
import clsx from "clsx";
import { useT } from "@app/contexts/preferences.context";
import { IcX } from "../icons";
import { btn, btnGhost, btnPrimary, iconBtn } from "../ui";

interface ConfirmModalProps {
  message: string;
  countdownSeconds?: number; // Số giây đếm ngược (optional)
  /** Tiêu đề hộp, mặc định "Xác nhận" */
  title?: string;
  /** Chữ nút đồng ý, mặc định "Đồng ý" */
  confirmLabel?: string;
  /** Hành động nguy hiểm (xoá…): nút đồng ý màu đỏ */
  danger?: boolean;
}

/**
 * Hộp xác nhận theo giao diện mới (cỡ chữ, nút giống bản thiết kế).
 * Giữ nguyên cách dùng cũ: close({ isAccept: true }) khi đồng ý, close() khi huỷ.
 */
function ConfirmModal({ portalDialogRef, portalData }: PortalDialogProps<ConfirmModalProps>) {
  const t = useT();
  const initialSeconds = portalData?.countdownSeconds ?? 0;

  // State lưu số giây còn lại
  const [timeLeft, setTimeLeft] = useState<number>(initialSeconds);

  useEffect(() => {
    // Nếu không truyền countdownSeconds hoặc truyền <= 0 thì không chạy timer
    if (!initialSeconds || initialSeconds <= 0) return;

    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timer);

          return 0;
        }

        return prev - 1;
      });
    }, 1000);

    // Cleanup timer khi component unmount
    return () => clearInterval(timer);
  }, [initialSeconds]);

  // Kiểm tra xem nút bấm có bị disable hay không
  const isButtonDisabled = timeLeft > 0;
  const label = t(portalData?.confirmLabel || "Đồng ý");
  const confirmLabel = isButtonDisabled ? `${label} (${timeLeft}s)` : label;
  const close = () => portalDialogRef.close();

  return (
    <Modal onCancel={close} buttonCancelInChildren={false}>
      <div className="flex w-[min(420px,calc(100vw-32px))] flex-col text-fg">
        <div className="flex items-start gap-3 border-b border-line px-[18px] pb-3 pt-4">
          <h2 className="flex-1 text-[17px] font-bold">{t(portalData?.title || "Xác nhận")}</h2>
          <button type="button" className={clsx(iconBtn, "!h-8 !w-8 flex-none")} aria-label={t("Đóng")} onClick={close}>
            <IcX className="h-4 w-4" />
          </button>
        </div>

        <p className="m-0 whitespace-pre-line px-[18px] py-4 text-sm leading-relaxed text-muted">
          {portalData?.message}
        </p>

        <div className="flex justify-end gap-2 border-t border-line px-[18px] py-3">
          <button type="button" className={clsx(btn, btnGhost)} onClick={close}>
            {t("Huỷ")}
          </button>
          <button
            type="button"
            disabled={isButtonDisabled} // Bị khóa khi đang đếm ngược
            className={clsx(btn, portalData?.danger ? "bg-bad text-white hover:brightness-110" : btnPrimary)}
            onClick={() => {
              if (!isButtonDisabled) {
                portalDialogRef.close({ isAccept: true });
              }
            }}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </Modal>
  );
}

export default ConfirmModal;
